import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import { loadLiveTimeline, MAX_WINDOW_DAYS, MAX_TIMELINE_PAGES } from './data';
import TempChart, { TempChartSkeleton } from './TempChart';
import SkeletonBlock from '../../components/atoms/Skeleton';
import TimeRangePicker from './TimeRangePicker';
import { SOURCE } from './timeline/model';
import toEpochSeconds from './timeline/timestamps';

// "What happened to this fridge" — driver actions, door/temp/safety, swipes and
// vends on one timeline. Read-only: queries only.

const DEFAULT_LOCATION = 'ICNC_LEFT';

// TODO: predates the theme-token + styledComponent convention that FleetStatus/
// follows. Converting is a standalone task; focus states already use theme tokens.
const COLORS = {
  primary: '#368007',
  danger: '#dc2626',
  warning: '#f59e0b',
  border: '#e5e7eb',
  muted: '#6b7280',
  bgCard: '#ffffff',
  bgPage: '#f9fafb',
  text: '#111827',
};

// Inline styles can't express :focus, so the focus treatment every interactive
// element on the page needs lives here, on the real theme tokens.
const StyledPage = styledComponent('div', {
  // No minHeight: the page sits inside a fixed-width .App.container, so pinning it
  // to 100vh sized the grey to the viewport instead of to the content.
  padding: theme.spacing.cardPadding,
  paddingBottom: theme.spacing.sectionGap,
  background: theme.colors.bgPage,
  color: theme.colors.textDark,

  '& input:focus, & button:focus': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  // Buttons carry their own background, so a border alone reads weakly on them.
  '& button:focus-visible': {
    outline: `2px solid ${theme.colors.infoBlue}`,
    outlineOffset: '2px',
  },
});
// Everything below this bar is scoped by it. The status banner above it is not —
// that's the fridge right now — and making that boundary visible is the bar's other
// job. Sticky, so the answer to "what am I looking at" survives a long scroll.
const StyledScopeBar = styledComponent('div', {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing.gapXl,
  padding: `${theme.spacing.gapLg} ${theme.spacing.gapXl}`,
  marginBottom: theme.spacing.fieldGap,
  background: theme.colors.bgCard,
  border: theme.borders.card,
  borderRadius: theme.radii.card,
  boxShadow: theme.shadows.card,

  // Separates the control that costs a fetch from the ones that only re-filter
  // rows already in memory. Same bar, because both answer "what am I looking at".
  '& .fsb-rule': {
    width: '1px',
    alignSelf: 'stretch',
    background: theme.colors.border,
  },
  '& .fsb-filters': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing.gapMd,
  },
  '& .fsb-check': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    ...theme.typography.body,
    color: theme.colors.textBody,
    whiteSpace: 'nowrap',
  },
  // Pushed to the far end: what came back, reported beside what was asked for.
  '& .fsb-coverage': {
    marginLeft: 'auto',
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    textAlign: 'right',
  },
});

const SEV_COLOR = {
  info: COLORS.muted,
  warning: COLORS.warning,
  critical: COLORS.danger,
};

function deriveStatus(fd) {
  if (!fd) return { label: 'UNKNOWN', color: COLORS.muted };
  if (fd.inaccessible) return { label: 'INACCESSIBLE', color: COLORS.warning };
  if (fd.safetyHold || fd.outOfOrder)
    return { label: 'DOWN', color: COLORS.danger };
  return { label: 'UP', color: COLORS.primary };
}

const fmtTime = (ts) => moment.unix(ts).format('MMM D, h:mm A');

// Both halves report a stop the same way: the oldest moment they actually reached.
// Paging walks backwards a page at a time, so stopping short means everything BEFORE
// that moment is missing. Why we stopped is worth separating: hitting our own page
// cap is a different thing to fix than a server that ran out of rows to hand back.
function describeTrim(what, since, atPageCap) {
  const from = since == null ? 0 : toEpochSeconds(since);
  const cause = atPageCap
    ? `the fridge is busy enough that ${MAX_TIMELINE_PAGES} pages didn't reach the start of your range — narrow the range to see the earlier part`
    : "the server stopped there and didn't say where to continue";
  return `${what} cover only ${
    from ? fmtTime(from) : 'part of your range'
  } onward — ${cause}.`;
}

// Four ways the range comes back short: either half stopped paging early, or either
// half's read failed. Name which — "may be incomplete" alone gives nobody a way to
// judge the timeline.
function describeGaps(gaps) {
  const parts = [];
  const eventsCut = gaps.find((g) => g.reason === 'events-truncated');
  const driverCut = gaps.find((g) => g.reason === 'driver-truncated');
  const eventsFailed = gaps.find((g) => g.reason === 'events-failed');
  const driverFailed = gaps.find((g) => g.reason === 'driver-failed');

  if (eventsFailed) parts.push(`Events: ${eventsFailed.message}`);
  if (driverFailed) {
    parts.push(
      `Driver actions: ${driverFailed.message} No row below is a restock, fix or unreachable report.`,
    );
  }
  if (eventsCut)
    parts.push(describeTrim('Events', eventsCut.since, eventsCut.atPageCap));
  if (driverCut)
    parts.push(
      describeTrim('Driver actions', driverCut.since, driverCut.atPageCap),
    );
  return parts.join(' ');
}

// What the range control reads. Built from the APPLIED range, never from what's
// currently typed: editing a field without searching must not change it, the same
// rule the row filter follows.
function describeRange(appliedPreset, appliedWindow) {
  const preset = PRESETS.find((p) => p.key === appliedPreset);
  if (preset) return preset.full;
  if (!appliedWindow) return 'Loading…';
  return `${fmtTime(appliedWindow.startSec)} → ${fmtTime(
    appliedWindow.endSec,
  )}`;
}

// One half's covered range. Unknown stays unknown: a half that didn't report is
// never quietly redrawn as the range we sent.
function describeCovered(what, c) {
  if (!c || c.since == null || c.until == null)
    return `${what} — covered range not reported`;
  return `${what} ${fmtTime(toEpochSeconds(c.since))} – ${fmtTime(
    toEpochSeconds(c.until),
  )}`;
}

// What came back, which is a different fact from what was asked for — the range
// control owns that one. Both halves now echo their own range, so neither is
// summarised as a bare "complete".
function describeCoverage(coverage) {
  return [
    describeCovered('events', coverage && coverage.events),
    describeCovered('driver actions', coverage && coverage.driver),
  ].join(' · ');
}

// Can a customer currently buy? Derived from the health snapshot.
function purchasesEnabled(fd) {
  if (!fd) return null;
  if (fd.inaccessible || fd.outOfOrder || fd.safetyHold) return false;
  const j = fd.jofemarStateV2 || {};
  if (j.vendAvailability && j.vendAvailability !== 'AVAILABLE') return false;
  if (j.serviceStatus === 'OUT_OF_ORDER') return false;
  return true;
}
// Total items across all slots in an inventory-snapshot payload.
function countInventory(raw) {
  if (!raw || !raw.itemsBySlot) return null;
  let t = 0;
  for (const s of Object.values(raw.itemsBySlot))
    t += ((s && s.itemsInSlot) || []).length;
  return t;
}

// Quick-range presets, ordered by how far back they reach, so the chips read as a
// scale. `today` is the one whose span varies — it sits ahead of 24h because that
// is the most it can ever cover. Each returns a start Date relative to now; To is now.
// `label` is the chip inside the picker, `full` is what the trigger reads when the
// range is applied — the trigger has room to spell it out and every reason to.
const PRESETS = [
  {
    key: '1h',
    label: '1h',
    full: 'Last hour',
    start: (n) => new Date(n.getTime() - 1 * 3600e3),
  },
  {
    key: '6h',
    label: '6h',
    full: 'Last 6 hours',
    start: (n) => new Date(n.getTime() - 6 * 3600e3),
  },
  {
    key: 'today',
    label: 'Today',
    full: 'Today so far',
    start: (n) => {
      const d = new Date(n);
      d.setHours(0, 0, 0, 0);
      return d;
    },
  },
  {
    key: '24h',
    label: '24h',
    full: 'Last 24 hours',
    start: (n) => new Date(n.getTime() - 24 * 3600e3),
  },
  {
    key: '3d',
    label: '3d',
    full: 'Last 3 days',
    start: (n) => new Date(n.getTime() - 3 * 86400e3),
  },
  {
    key: '7d',
    label: '7d',
    full: 'Last 7 days',
    start: (n) => new Date(n.getTime() - 7 * 86400e3),
  },
];

// Date -> value string for <input type="datetime-local"> ("YYYY-MM-DDTHH:mm").
function toLocalInput(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
}

export default function FridgeHealthDiagnostics(props) {
  const routeLoc =
    props.match && props.match.params ? props.match.params.locationId : null;
  // Derived from the route, not state: a failed load must not leave this naming
  // the previous fridge while the URL — the source of truth below — names the new one.
  const location = routeLoc || DEFAULT_LOCATION;
  const [input, setInput] = useState(routeLoc || DEFAULT_LOCATION);
  const [fridgeData, setFridgeData] = useState(null);
  // Split at ingest (see buildTimeline): `beats` are the rows, `series` the curves.
  // Nothing downstream re-derives which is which.
  const [beats, setBeats] = useState([]);
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Non-empty when the timeline is missing rows it can't enumerate — driver-action
  // queries cut short or failed, or a server-side clamp on the event range. Never
  // silently swallowed — see data.js.
  const [gaps, setGaps] = useState([]);
  // The server's echo of the range it actually covered, and whether the user chose
  // the range at all. Kept apart from the Since/To boxes on purpose.
  const [coverage, setCoverage] = useState(null);
  const [userWindow, setUserWindow] = useState(false);
  // The preset behind the range that was applied. `activePreset` below tracks the
  // chip the user has *selected* in the picker, which can differ until they search.
  const [appliedPreset, setAppliedPreset] = useState('today');
  // What `fridgeData` actually describes, so a fridge change can be told from a
  // range change. A ref, not state: loadLive must not be rebuilt when it moves.
  const loadedLocationRef = useRef(null);
  // Ticket taken by each loadLive call, so a response can tell whether anyone is
  // still waiting on it: a slow 7d fetch must not land on top of a faster 1h one.
  const requestIdRef = useRef(0);
  // The window the loaded data was fetched with — always concrete after a load,
  // since the default view resolves to today so far. Null only before the first
  // load and after a failed one.
  const [appliedWindow, setAppliedWindow] = useState(null);
  const [activeCats, setActiveCats] = useState(null); // null = all
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [chartOpen, setChartOpen] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [fromStr, setFromStr] = useState('');
  const [toStr, setToStr] = useState('');
  const [activePreset, setActivePreset] = useState('today');

  const loadLive = useCallback(async (loc, win = null, presetKey = null) => {
    requestIdRef.current += 1;
    const ticket = requestIdRef.current;
    const isCurrent = () => requestIdRef.current === ticket;
    setLoading(true);
    setError(null);
    setGaps([]);
    // A new fridge makes the banner wrong, not merely stale — it would name the
    // previous one for the whole round trip. A new range doesn't: the snapshot it
    // shows is the fridge now, and carries its own "as of". So only the first drops.
    if (loadedLocationRef.current !== loc) setFridgeData(null);
    try {
      const {
        fridgeData: fd,
        timeline: tl,
        gaps: g,
        coverage: cov,
        resolvedWindow,
      } = await loadLiveTimeline(loc, { window: win });
      if (!isCurrent()) return;
      setFridgeData(fd);
      setBeats(tl.beats);
      setSeries(tl.series);
      setGaps(g || []);
      setCoverage(cov || null);
      // The range the fetch actually used — concrete even when the caller passed
      // none. The row filter and the caption both read this, never the Since/To
      // boxes, so neither can claim a range we didn't query.
      setAppliedWindow(resolvedWindow || null);
      setUserWindow(Boolean(win));
      // No window means the default, and the default IS the `today` preset — same
      // midnight-to-now range — so the control can name it rather than sit blank.
      setAppliedPreset(win ? presetKey : 'today');
      loadedLocationRef.current = loc;
    } catch (e) {
      if (!isCurrent()) return;
      setError((e && e.message) || 'Failed to load fridge timeline.');
      setBeats([]);
      setSeries({});
      setFridgeData(null);
      setCoverage(null);
      setAppliedWindow(null);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The URL is the one source of truth for which fridge is shown: submitting the
    // selector navigates, and this effect is what loads. Back/forward therefore
    // work, and the box follows the URL rather than keeping the old typed value.
    setInput(routeLoc || DEFAULT_LOCATION);
    loadLive(routeLoc || DEFAULT_LOCATION);
  }, [loadLive, routeLoc]);

  // Re-submitting the fridge already in the URL is a refresh, not a navigation —
  // pushing it would add a history entry that changes nothing and never reload.
  const submitLocation = () => {
    const next = input.trim();
    if (!next) return;
    if (next === (routeLoc || DEFAULT_LOCATION)) loadLive(next);
    else props.history.push(`/fridge-health/timeline/${next}`);
  };

  const nowSec = () => Math.floor(Date.now() / 1000);
  const localToSec = (s) =>
    s ? Math.floor(new Date(s).getTime() / 1000) : null;
  const fromSec = localToSec(fromStr);
  const toSecVal = localToSec(toStr);

  // The server answers at most MAX_WINDOW_DAYS per request and we page back from
  // there, so a longer range is reachable but costs a round trip per week. The
  // picker holds the line at one page's worth rather than spending them silently.
  const windowBounds = () => {
    const ceilingSec = nowSec();
    return { floorSec: ceilingSec - MAX_WINDOW_DAYS * 86400, ceilingSec };
  };

  // Since-only means "up to now". To-only has no such reading, so it stays
  // unapplied and shows a hint rather than a guessed start. Either end is
  // clamped into the window: `min`/`max` below only constrain the picker, and a
  // typed or pasted value ignores them entirely.
  const searchWindow = () => {
    if (!fromSec) return null;
    const { floorSec, ceilingSec } = windowBounds();
    const startSec = Math.max(fromSec, floorSec);
    const endSec = Math.min(toSecVal || ceilingSec, ceilingSec);
    // A start clamped past the end would invert the range, and an empty one is
    // rejected now rather than answered with nothing — so keep at least a second.
    return { startSec, endSec: Math.max(endSec, startSec + 1) };
  };
  const toWithoutSince = Boolean(toSecVal && !fromSec);
  // `min`/`max` on the inputs don't stop this: moving Since past an existing To,
  // or pasting a value, gets there. Nothing usable can be loaded or shown for it.
  const reversedRange = Boolean(fromSec && toSecVal && toSecVal < fromSec);

  const bounds = windowBounds();
  const windowMinStr = toLocalInput(new Date(bounds.floorSec * 1000));
  const windowMaxStr = toLocalInput(new Date(bounds.ceilingSec * 1000));
  // One minute of slack: the inputs carry minute resolution, so a value set by
  // the 7d preset sits a few seconds outside an exact boundary and would
  // otherwise report itself as trimmed the moment it was applied.
  const windowClamped =
    (fromSec !== null && fromSec < bounds.floorSec - 60) ||
    (toSecVal !== null && toSecVal > bounds.ceilingSec + 60);

  const onSearch = () => loadLive(location, searchWindow(), null);

  const applyPreset = (key) => {
    const preset = PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const now = new Date();
    const start = preset.start(now);
    setFromStr(toLocalInput(start));
    setToStr(toLocalInput(now));
    setActivePreset(key);
    // Pass the window explicitly (state updates above are async).
    loadLive(
      location,
      {
        startSec: Math.floor(start.getTime() / 1000),
        endSec: Math.floor(now.getTime() / 1000),
      },
      key,
    );
  };

  // Back to the default range, which is today so far.
  const resetWindow = () => {
    setFromStr('');
    setToStr('');
    setActivePreset('today');
    loadLive(location, null);
  };

  // Built from beats alone, so a curve can never turn up as a row filter — which is
  // how the temp chip used to sit among the row filters and silently hide the chart.
  const categories = Array.from(new Set(beats.map((e) => e.category)));

  // Keeps rows inside the requested range, which the server's own `[since, until)`
  // already is — so this mostly guards the snapshot and heartbeat rows that don't
  // come from it. Reads `appliedWindow` — the range actually fetched — and never the
  // Since/To boxes: typing a bound without searching must not hide loaded rows.
  // Snapshot rows are exempt because they describe the fridge now, not something
  // inside the range; an active safety hold has to stay visible in a past window.
  const inWindow = (e) => {
    if (!userWindow || !appliedWindow || e.source === SOURCE.SNAPSHOT)
      return true;
    return e.ts >= appliedWindow.startSec && e.ts < appliedWindow.endSec;
  };
  const inCats = (e) => !activeCats || activeCats.has(e.category);

  const visible = beats.filter(
    (e) => !(issuesOnly && e.severity === 'info') && inCats(e) && inWindow(e),
  );
  // Only the window applies. The row filters and "Issues only" both describe which
  // rows to show, and the trend is the context those rows are read against, not one
  // more row — so neither can reach it.
  const tempSamples = (series.TEMP || []).filter(inWindow);
  // Newest first: the question people bring to this page is "what just happened",
  // so the answer should be the first row, not the last. Only the rows flip — the
  // chart stays ascending, because that is what a left-to-right time axis means,
  // and `visible` stays ascending for the summary tiles that read its last entry —
  // hence the copy: sort() is in place, and sorting `visible` itself would hand
  // those tiles the oldest inventory snapshot instead of the newest.
  const rows = [...visible].sort(
    (a, b) =>
      b.ts - a.ts ||
      a.source.localeCompare(b.source) ||
      a.type.localeCompare(b.type),
  );
  // Passed through raw: the snapshot's setpoint can carry a unit ("0.5C") the
  // readings don't, and only the chart knows whether the two share an axis.
  const setpointRaw = fridgeData ? fridgeData.temperatureSetpoint : null;

  // AM/CS summary, computed over the in-view set.
  const purchasable = purchasesEnabled(fridgeData);
  // Only LocationStocked counts — packing-slip submissions must not inflate this.
  const stockingEvents = visible.filter((e) => e.type === 'LOCATION_STOCKED');
  const lastStock = stockingEvents.length
    ? Math.max(...stockingEvents.map((e) => e.ts))
    : null;
  const invEvents = visible.filter((e) => e.type === 'INVENTORY');
  const itemsInFridge = invEvents.length
    ? countInventory(invEvents[invEvents.length - 1].raw)
    : null;

  const status = deriveStatus(fridgeData);
  const toggleCat = (cat) => {
    const next = new Set(activeCats || categories);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    setActiveCats(next);
  };

  // Scoped to what's on screen: expanding rows the filters have hidden would put
  // JSON for events the user isn't looking at into cmd+F's results.
  const allExpanded = rows.length > 0 && rows.every((e) => expanded[e.id]);
  const toggleAll = () => {
    if (allExpanded) {
      setExpanded({});
      return;
    }
    const next = {};
    rows.forEach((e) => {
      next[e.id] = true;
    });
    setExpanded(next);
  };

  return (
    <StyledPage>
      {/* Named destination rather than history.goBack(): this page is deep-linked
          from Fleet Status rows and from bookmarks, and browser-back would either
          leave the app or land somewhere unrelated. */}
      <Link to="/fleetstatus" style={backLink}>
        <span aria-hidden>←</span> Fleet Status
      </Link>
      {/* The fridge selector, not the range control, belongs beside the title: it
          changes the whole page including the status banner below. The range only
          scopes what sits under the bar further down, so it lives there instead. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <h2 style={{ margin: '0 0 4px' }}>Fridge Timeline</h2>
          <div style={{ color: COLORS.muted }}>
            What happened to this fridge — one timeline.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label
            htmlFor="fhd-location"
            style={{
              ...lbl,
              marginBottom: 0,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <span>Fridge</span>
            <input
              id="fhd-location"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitLocation()}
              placeholder="locationId"
              style={{ ...inputStyle, width: 160 }}
            />
          </label>
          <button
            type="button"
            onClick={submitLocation}
            style={btn(COLORS.primary)}
          >
            Load
          </button>
        </div>
      </div>

      {/* Status banner (full width) */}
      {loading && !fridgeData && <StatusBannerSkeleton />}
      {fridgeData && (
        <div style={card}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {fridgeData.prettyName || location}
            </span>
            <span style={badge(status.color)}>{status.label}</span>
            <span style={{ color: COLORS.muted }}>
              {fridgeData.temperature != null &&
                `Temp ${fridgeData.temperature}°`}
              {fridgeData.temperatureSetpoint != null &&
                ` / setpoint ${fridgeData.temperatureSetpoint}°`}
            </span>
            {fridgeData.timestamp ? (
              <span style={{ color: COLORS.muted, marginLeft: 'auto' }}>
                as of {fmtTime(Number(fridgeData.timestamp))}
              </span>
            ) : null}
          </div>
          {Array.isArray(fridgeData.allReasons) &&
            fridgeData.allReasons.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {fridgeData.allReasons.map((r) => (
                  <span key={r} style={reasonPill}>
                    {r}
                  </span>
                ))}
              </div>
            )}
        </div>
      )}

      {/* Same set, colors and destinations as the Fleet Status row actions, so the
          pages cross-link instead of forcing a trip back through the dashboard.
          Deliberately outside the status card: a fridge with no health row renders
          no card, and that's exactly when someone wants to jump to Health/Detail.
          Keyed off the routed fridge, not the loaded one, so a fridge that fails to
          load still links to the fridge you asked for. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <span style={{ ...lbl, marginBottom: 0 }}>{location}</span>
        <Link
          style={miniBtn(theme.colors.infoBlue)}
          to={`/locations/health/${location}`}
        >
          Health
        </Link>
        <Link
          style={miniBtn(theme.colors.accentAmber)}
          to={`/locations/detail/${location}`}
        >
          Detail
        </Link>
        <Link
          style={miniBtn(theme.colors.accentPurple)}
          to={`/locations/inventory/${location}`}
        >
          Inventory
        </Link>
      </div>

      {/* Scope bar: the range that was applied, the filters on top of it, and what
          actually came back — one band, directly above everything the three govern. */}
      <StyledScopeBar>
        <TimeRangePicker
          label={describeRange(appliedPreset, appliedWindow)}
          presets={PRESETS}
          activePreset={activePreset}
          onPreset={applyPreset}
          fromStr={fromStr}
          toStr={toStr}
          onFromChange={(v) => {
            setFromStr(v);
            setActivePreset(null);
          }}
          onToChange={(v) => {
            setToStr(v);
            setActivePreset(null);
          }}
          minStr={windowMinStr}
          maxStr={windowMaxStr}
          toWithoutSince={toWithoutSince}
          reversedRange={reversedRange}
          clampNotice={
            windowClamped
              ? `Searches cover the last ${MAX_WINDOW_DAYS} days — the search will use the part of your range that falls inside them.`
              : null
          }
          onSearch={onSearch}
          onReset={resetWindow}
        />
        <span className="fsb-rule" aria-hidden />
        <div className="fsb-filters">
          <label className="fsb-check" htmlFor="fhd-issues-only">
            <input
              id="fhd-issues-only"
              type="checkbox"
              checked={issuesOnly}
              onChange={(e) => setIssuesOnly(e.target.checked)}
            />
            <span>Issues only</span>
          </label>
          {categories.map((c) => {
            const on = !activeCats || activeCats.has(c);
            return (
              <button
                type="button"
                key={c}
                onClick={() => toggleCat(c)}
                style={chip(on)}
                aria-pressed={on}
              >
                {c}
              </button>
            );
          })}
        </div>
        {!loading && (beats.length > 0 || tempSamples.length > 0) && (
          <span className="fsb-coverage">{describeCoverage(coverage)}</span>
        )}
      </StyledScopeBar>

      {/* High-level summary tiles (AM/CS): common reporting areas at a glance */}
      {loading && <StatTilesSkeleton />}
      {!loading && fridgeData && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <Stat
            label="Food Safety Hold"
            value={fridgeData.safetyHold ? 'YES' : 'No'}
            tone={fridgeData.safetyHold ? 'bad' : 'good'}
          />
          <Stat
            label="Purchases"
            value={
              purchasable == null ? '—' : purchasable ? 'Enabled' : 'Disabled'
            }
            tone={purchasable == null ? null : purchasable ? 'good' : 'bad'}
          />
          <Stat
            label="Stockings (in view)"
            value={String(stockingEvents.length)}
          />
          <Stat
            label="Last stocked"
            value={lastStock ? fmtTime(lastStock) : '—'}
          />
          <Stat
            label="Items in fridge"
            value={itemsInFridge != null ? String(itemsInFridge) : '—'}
          />
        </div>
      )}

      {/* The filters moved into the scope bar, so the timeline gets the page. */}
      <main>
        {loading && (
          <>
            <TempChartSkeleton />
            <TimelineSkeleton />
          </>
        )}
        {error && (
          <div style={{ color: COLORS.danger }}>
            <span role="img" aria-label="Error">
              ⚠️
            </span>{' '}
            {error}
          </div>
        )}
        {/* Whatever either half admits it left out. Saying so is the whole point:
              a timeline that renders cleanly while missing a restock is worse than
              one that admits the gap. */}
        {!loading && !error && gaps.length > 0 && (
          <div style={gapNotice} role="status">
            <span role="img" aria-label="Warning">
              ⚠️
            </span>{' '}
            {describeGaps(gaps)}
          </div>
        )}
        {!loading &&
          !error &&
          beats.length === 0 &&
          tempSamples.length === 0 && (
            <div style={{ color: COLORS.muted }}>
              No timeline events for this fridge in the selected window.
            </div>
          )}

        {/* Above the rows: the trend is what everything below is read against. */}
        {!loading && !error && tempSamples.length > 0 && (
          <TempChart
            samples={tempSamples}
            setpointRaw={setpointRaw}
            open={chartOpen}
            onToggle={() => setChartOpen((v) => !v)}
          />
        )}

        {/* Counts sit between the chart and the rows, since they describe both.
            Hidden while loading: until the fetch lands they would be the previous
            load's numbers sitting over the new one's skeletons. */}
        {!loading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            {/* Each number names what it counts: rows and charted readings are
                different views of the same load, not parts of one ratio. */}
            <span style={{ fontWeight: 600 }}>
              {`${rows.length.toLocaleString()} rows`}
              {` · ${beats.length.toLocaleString()} events loaded`}
            </span>
            {rows.length > 0 && (
              // cmd+F only finds rendered text, and a collapsed payload isn't in
              // the DOM — so opening all rows is what makes the JSON searchable.
              <button
                type="button"
                onClick={toggleAll}
                style={expandAllBtn}
                aria-pressed={allExpanded}
              >
                {allExpanded ? 'Collapse all details' : 'Expand all details'}
              </button>
            )}
          </div>
        )}

        {/* Temperature alone is a real result, not an empty timeline — say which.
            The filters never touch the chart, so this names it as unaffected rather
            than as something that happened to match. */}
        {!loading && !error && beats.length > 0 && rows.length === 0 && (
          <div style={{ color: COLORS.muted }}>
            {tempSamples.length > 0
              ? 'No events match the current filters — the temperature chart above is unaffected.'
              : 'No events match the current filters.'}
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div>
            {rows.map((e, i) => {
              const sev = SEV_COLOR[e.severity] || COLORS.muted;
              const isFirst = i === 0;
              const isLast = i === rows.length - 1;
              const isOpen = !!expanded[e.id];
              return (
                <div key={e.id} style={tlRow}>
                  <div style={tlTime}>{fmtTime(e.ts)}</div>
                  <div style={tlRail}>
                    {!isFirst && <div style={lineSeg} />}
                    {!isLast && <div style={lineSegBottom} />}
                    <div style={tlDot(sev)} />
                  </div>
                  <div style={tlCard(e.severity)}>
                    {/* A real button, not a clickable row: expanding is the only way
                          to read an event's raw payload, and on a div it was reachable
                          by mouse only — no tab stop, no Enter/Space, nothing announced. */}
                    <button
                      type="button"
                      style={tlToggle}
                      aria-expanded={isOpen}
                      onClick={() =>
                        setExpanded((x) => ({ ...x, [e.id]: !x[e.id] }))
                      }
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        {e.type === 'VEND_KIOSK' ? (
                          <img
                            src="/favicon.ico"
                            alt="Farmer's Fridge kiosk"
                            width={18}
                            height={18}
                            style={{
                              borderRadius: 3,
                              verticalAlign: 'middle',
                            }}
                          />
                        ) : e.type === 'SWIPE' &&
                          e.raw &&
                          /stripe/i.test(e.raw.customerName || '') ? (
                          <span title="Stripe" style={stripeMark}>
                            S
                          </span>
                        ) : (
                          <span aria-hidden>{e.icon}</span>
                        )}
                        <span
                          style={{
                            fontWeight: 600,
                            color: e.severity === 'info' ? COLORS.text : sev,
                          }}
                        >
                          {e.title}
                        </span>
                        <span
                          style={{
                            marginLeft: 'auto',
                            fontSize: 11,
                            color: COLORS.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 0.3,
                          }}
                        >
                          {e.category}
                        </span>
                        <span
                          aria-hidden
                          style={{ color: COLORS.muted, fontSize: 11 }}
                        >
                          {isOpen ? '▾' : '▸'}
                        </span>
                      </span>
                      {e.summary && (
                        <span
                          style={{
                            display: 'block',
                            color: COLORS.muted,
                            fontSize: 13,
                            marginTop: 2,
                          }}
                        >
                          {e.summary}
                        </span>
                      )}
                    </button>
                    {isOpen && (
                      <pre style={pre}>{JSON.stringify(e.raw, null, 2)}</pre>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </StyledPage>
  );
}

FridgeHealthDiagnostics.propTypes = {
  // `locationId` is optional in the route; absent falls back to DEFAULT_LOCATION.
  match: PropTypes.shape({
    params: PropTypes.shape({
      locationId: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

FridgeHealthDiagnostics.defaultProps = {
  match: null,
};

// Reuses `card`, the real banner's own style, so the reserved height is the real
// height by construction. The `allReasons` pills are a conditional second row and
// are not reserved — see the same trade in TempChartSkeleton.
function StatusBannerSkeleton() {
  return (
    <div style={card} aria-hidden>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <SkeletonBlock width={120} height={18} />
        <SkeletonBlock width={54} height={18} radius="999px" />
        <SkeletonBlock width={180} height={14} />
        <div style={{ marginLeft: 'auto' }}>
          <SkeletonBlock width={140} height={14} />
        </div>
      </div>
    </div>
  );
}

// Same five tiles, same `statTile` box. Standing in for them also stops the real
// ones reporting the PREVIOUS range's counts while a new range is in flight —
// "Stockings (in view)" would otherwise name a view you already left.
function StatTilesSkeleton() {
  return (
    <div
      style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}
      aria-hidden
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} style={statTile}>
          <SkeletonBlock width={88} height={11} />
          <div style={{ marginTop: 6 }}>
            <SkeletonBlock width={56} height={16} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Loading state that mirrors the timeline layout, so the page previews its shape.
function TimelineSkeleton() {
  const rows = [0, 1, 2, 3, 4];
  return (
    <div>
      <style>{`
        @keyframes ffPulse { 0%, 100% { transform: scale(1); opacity: .55; } 50% { transform: scale(1.3); opacity: 1; } }
      `}</style>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: COLORS.muted,
          marginBottom: 14,
          fontWeight: 600,
        }}
      >
        <span style={spinnerDot(0)} />
        <span style={spinnerDot(0.15)} />
        <span style={spinnerDot(0.3)} />
        <span style={{ marginLeft: 4 }}>Reconstructing the timeline…</span>
      </div>
      {rows.map((i) => (
        <div key={i} style={tlRow}>
          <div style={{ ...tlTime, paddingTop: 14 }}>
            <SkeletonBlock width={58} height={10} />
          </div>
          <div style={tlRail}>
            {i !== 0 && <div style={lineSeg} />}
            {i !== rows.length - 1 && <div style={lineSegBottom} />}
            <div
              style={{
                ...tlDot(COLORS.primary),
                animation: `ffPulse 1.2s ${i * 0.15}s infinite ease-in-out`,
              }}
            />
          </div>
          <div style={{ ...tlCard('info') }}>
            <SkeletonBlock width="40%" />
            <div style={{ marginTop: 8 }}>
              <SkeletonBlock width="70%" height={10} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function spinnerDot(delay) {
  return {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: COLORS.primary,
    display: 'inline-block',
    animation: `ffPulse 1s ${delay}s infinite ease-in-out`,
  };
}

function Stat({ label, value, tone }) {
  const color =
    tone === 'bad'
      ? COLORS.danger
      : tone === 'good'
      ? COLORS.primary
      : COLORS.text;
  return (
    <div style={statTile}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          color: COLORS.muted,
          letterSpacing: 0.3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  // 'good' | 'bad'; null means neutral (e.g. a value we couldn't determine).
  tone: PropTypes.string,
};

Stat.defaultProps = {
  tone: null,
};

const RAIL_W = 28;
// `color` is explicit because the date inputs now sit inside their <label>, which
// sets a muted color the input would otherwise inherit.
const inputStyle = {
  padding: '6px 10px',
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  fontSize: 14,
  color: COLORS.text,
};
const card = {
  background: COLORS.bgCard,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
};
// minHeight pins the tile and its skeleton to one height; without it the stand-in
// matches only as closely as two guesses about line boxes happen to agree.
const statTile = {
  background: COLORS.bgCard,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: '10px 14px',
  minWidth: 120,
  minHeight: 54,
  boxSizing: 'border-box',
};
const stripeMark = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: 4,
  background: '#635BFF',
  color: '#fff',
  fontSize: 11,
  fontWeight: 800,
};
const pre = {
  background: '#f3f4f6',
  padding: 10,
  borderRadius: 8,
  fontSize: 12,
  overflowX: 'auto',
  marginTop: 6,
};
const reasonPill = {
  display: 'inline-block',
  background: '#fef2f2',
  color: COLORS.danger,
  border: `1px solid ${COLORS.danger}`,
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 12,
  marginRight: 6,
};
const lbl = {
  display: 'block',
  fontSize: 12,
  color: COLORS.muted,
  marginBottom: 4,
};
// New styles use theme tokens rather than the local COLORS map above — the file's
// eventual conversion has one less thing to unpick.
const backLink = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 10,
  color: theme.colors.primary,
  textDecoration: 'none',
  fontSize: theme.sizes.smallFontSize,
  fontWeight: theme.fontWeights.semibold,
};
// Mirrors `.fs-minibtn` on Fleet Status: tinted outline, tinted text, card fill.
function miniBtn(color) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: theme.radii.badge,
    fontSize: '11px',
    fontWeight: theme.fontWeights.semibold,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    border: `1px solid ${color}55`,
    color,
    background: theme.colors.bgCard,
  };
}
const expandAllBtn = {
  padding: '2px 8px',
  borderRadius: theme.radii.badge,
  fontSize: '11px',
  fontWeight: theme.fontWeights.semibold,
  border: `1px solid ${theme.colors.infoBlue}55`,
  color: theme.colors.infoBlue,
  background: theme.colors.bgCard,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const gapNotice = {
  background: '#fff7ed',
  border: '1px solid #fed7aa',
  color: '#7c2d12',
  borderRadius: 8,
  padding: '8px 12px',
  marginBottom: 12,
  fontSize: 13,
};

// The row itself is no longer interactive — the disclosure button inside it is.
const tlRow = { display: 'flex', alignItems: 'stretch', gap: 12 };
// Reset the native button chrome so the disclosure still reads as a timeline card.
const tlToggle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
};
const tlTime = {
  width: 92,
  flexShrink: 0,
  textAlign: 'right',
  color: COLORS.muted,
  fontSize: 12,
  paddingTop: 12,
};
const tlRail = { width: RAIL_W, flexShrink: 0, position: 'relative' };
const lineSeg = {
  position: 'absolute',
  left: RAIL_W / 2 - 1,
  top: 0,
  height: 18,
  width: 2,
  background: COLORS.border,
};
const lineSegBottom = {
  position: 'absolute',
  left: RAIL_W / 2 - 1,
  top: 18,
  bottom: 0,
  width: 2,
  background: COLORS.border,
};
function tlDot(color) {
  return {
    position: 'absolute',
    left: RAIL_W / 2 - 6,
    top: 12,
    width: 12,
    height: 12,
    borderRadius: 999,
    background: color,
    border: '2px solid #fff',
    boxShadow: `0 0 0 1px ${COLORS.border}`,
  };
}
function tlCard(sev) {
  const base = {
    flex: 1,
    minWidth: 0,
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 12,
  };
  if (sev === 'critical')
    return { ...base, borderLeft: `3px solid ${COLORS.danger}` };
  if (sev === 'warning')
    return { ...base, borderLeft: `3px solid ${COLORS.warning}` };
  return base;
}
function btn(bg) {
  return {
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 14,
  };
}
function chip(on) {
  return {
    background: on ? COLORS.primary : '#fff',
    color: on ? '#fff' : COLORS.muted,
    border: `1px solid ${on ? COLORS.primary : COLORS.border}`,
    borderRadius: 999,
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: 13,
  };
}
function badge(color) {
  return {
    background: color,
    color: '#fff',
    borderRadius: 999,
    padding: '2px 12px',
    fontSize: 13,
    fontWeight: 600,
  };
}
